import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FileText, LayoutDashboard, Car, Route, Calendar, LogOut } from 'lucide-react';
import { Button } from '../../components/ui/button';
import api from '../../utils/api';
import { toast } from 'sonner';

function AdminBookings() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await api.get('/admin/bookings');
      setBookings(response.data);
    } catch (error) {
      toast.error('Failed to fetch bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
      localStorage.removeItem('token');
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      localStorage.removeItem('token');
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-neutral-background flex">
      <div className="w-64 bg-primary text-white p-6">
        <div className="mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_0cf4ab96-b53e-403e-86bf-50fb1ab117ef/artifacts/no4fnv8f_Screenshot_20260105_125050_Samsung%20Notes.jpg"
            alt="Hasaka Tours & Travels"
            className="h-16 w-auto mb-3 brightness-0 invert"
          />
          <h2 className="font-manrope font-bold text-xl">Admin Panel</h2>
        </div>
        <nav className="space-y-2">
          <Link to="/admin" className="flex items-center gap-3 px-4 py-3 rounded-sm hover:bg-primary-900 text-slate-200 hover:text-white font-medium">
            <LayoutDashboard className="w-5 h-5" />
            Dashboard
          </Link>
          <Link to="/admin/vehicles" className="flex items-center gap-3 px-4 py-3 rounded-sm hover:bg-primary-900 text-slate-200 hover:text-white font-medium">
            <Car className="w-5 h-5" />
            Vehicles
          </Link>
          <Link to="/admin/routes" className="flex items-center gap-3 px-4 py-3 rounded-sm hover:bg-primary-900 text-slate-200 hover:text-white font-medium">
            <Route className="w-5 h-5" />
            Routes
          </Link>
          <Link to="/admin/schedules" className="flex items-center gap-3 px-4 py-3 rounded-sm hover:bg-primary-900 text-slate-200 hover:text-white font-medium">
            <Calendar className="w-5 h-5" />
            Schedules
          </Link>
          <Link to="/admin/bookings" className="flex items-center gap-3 px-4 py-3 rounded-sm bg-primary-900 text-white font-medium">
            <FileText className="w-5 h-5" />
            Bookings
          </Link>
        </nav>
        <div className="mt-auto pt-12">
          <Button onClick={() => navigate('/dashboard')} variant="outline" className="w-full mb-3 border-white text-white hover:bg-white hover:text-primary rounded-sm">
            User Dashboard
          </Button>
          <Button onClick={handleLogout} variant="ghost" className="w-full text-white hover:bg-primary-900 rounded-sm">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      <div className="flex-1 p-12">
        <div className="mb-8">
          <h1 className="font-manrope font-bold text-4xl text-primary mb-2" data-testid="bookings-heading">All Bookings</h1>
          <p className="text-slate-600 font-public-sans text-lg">View and manage all customer bookings</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-neutral-text-secondary font-public-sans">Loading bookings...</p>
            </div>
          </div>
        ) : bookings.length === 0 ? (
          <div className="bg-white rounded-sm p-12 text-center shadow-sm border border-slate-200">
            <FileText className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600 font-public-sans text-lg">No bookings found.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {bookings.map((booking) => (
              <div key={booking.booking_id} className="bg-white rounded-sm p-6 shadow-sm border border-slate-200" data-testid={`booking-${booking.booking_id}`}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="font-mono font-bold text-primary text-lg">#{booking.booking_id.slice(-8)}</span>
                      <span
                        className={`px-3 py-1 rounded-sm text-xs font-semibold uppercase tracking-wide ${
                          booking.booking_status === 'confirmed'
                            ? 'bg-green-100 text-green-700'
                            : booking.booking_status === 'pending'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {booking.booking_status}
                      </span>
                      <span
                        className={`px-3 py-1 rounded-sm text-xs font-semibold uppercase tracking-wide ${
                          booking.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                        }`}
                      >
                        Payment: {booking.payment_status}
                      </span>
                    </div>
                    <div className="grid grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-slate-500 font-public-sans">Contact Email:</span>
                        <p className="font-semibold text-primary">{booking.contact_email}</p>
                      </div>
                      <div>
                        <span className="text-slate-500 font-public-sans">Phone:</span>
                        <p className="font-semibold text-primary">{booking.contact_phone}</p>
                      </div>
                      <div>
                        <span className="text-slate-500 font-public-sans">Passengers:</span>
                        <p className="font-semibold text-primary">{booking.passengers.length}</p>
                      </div>
                      <div>
                        <span className="text-slate-500 font-public-sans">Seats:</span>
                        <p className="font-semibold text-primary">{booking.booked_seats.join(', ')}</p>
                      </div>
                    </div>
                    <div className="mt-3 bg-slate-50 rounded-sm p-3">
                      <p className="text-xs text-slate-500 mb-2 uppercase tracking-wide">Passenger Details:</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {booking.passengers.map((passenger, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <span className="font-mono font-bold text-secondary">{passenger.seat}:</span>
                            <span className="font-public-sans text-slate-700">
                              {passenger.name}, {passenger.age}y, {passenger.gender}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="ml-6 text-right">
                    <p className="text-sm text-slate-500 mb-1">Total Amount</p>
                    <p className="font-manrope text-3xl font-bold text-secondary">${booking.total_amount.toFixed(2)}</p>
                    <p className="text-xs text-slate-500 mt-2">{new Date(booking.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminBookings;
